import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Play, Pause, Download, Plus, Trash2, Loader2, Save, Copy, Clipboard, CheckCircle, X } from 'lucide-react'
import { ModelViewer, resolveModelSource } from '@/components/ModelViewer'
import { useAdminModel } from '@/hooks/useModels'
import { useAuth } from '@/hooks/useAuth'
import { getCameraAtTime } from '@/utils/cameraAnimation'
import { cameraStateToQuaternion, quaternionToCameraState, migrateCameraState, quaternionToEulerDegrees, eulerDegreesToQuaternion } from '@/utils/cameraConversion'
import type { CameraState } from '@/types/camera'
import type { Quaternion } from '@/utils/quaternion'
import { CanvasRecorder, type RecordingProgress } from '@/utils/recorder'
import { mediaApi } from '@/services/api/media'

interface Keyframe {
  id: string
  time: number // 0-5 seconds
  camera: CameraState
}

export function ThumbnailGenerator() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { data: modelResponse, isLoading } = useAdminModel(id!)
  const model = modelResponse?.data
  const { token } = useAuth()
  
  
  // Determine where to go back based on where user came from
  const backPath = location.state?.from || `/admin/models/${id}/edit`
  
  const [keyframes, setKeyframes] = useState<Keyframe[]>([])
  const [currentTime, setCurrentTime] = useState(0)
  const [timeInputValue, setTimeInputValue] = useState('0.00')
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [selectedKeyframe, setSelectedKeyframe] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState<RecordingProgress | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [exportQuality, setExportQuality] = useState<'low' | 'medium' | 'high'>('medium')
  const [cameraPosition, setCameraPosition] = useState<[number, number, number]>([0, 2, 5])
  const [cameraQuaternion, setCameraQuaternion] = useState<Quaternion>({ w: 1, x: 0, y: 0, z: 0 })
  const [hasInitialized, setHasInitialized] = useState(false)
  const [editingKeyframe, setEditingKeyframe] = useState<Keyframe | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [copiedKeyframe, setCopiedKeyframe] = useState<Keyframe | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [hasCameraInitialized, setHasCameraInitialized] = useState(false)
  
  const timelineRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const recorderRef = useRef<CanvasRecorder | null>(null)
  const dualgsViewerRef = useRef<{
    setRenderSize?: (w: number, h: number) => void
    clearRenderSize?: () => void
  } | null>(null)
  
  const TIMELINE_DURATION = 5 // seconds
  
  // Memoize values before any conditional returns to avoid React hooks error.
  // Routing (single / ply / sequence / clipgstream) + URL derivation all live
  // in resolveModelSource; ModelViewer picks the right viewer from this.
  const source = useMemo(
    () => (model ? resolveModelSource(model) : null),
    [model?.filePath, model?.id, model?.metadata],
  )
  
  const defaultCamera = useMemo(() => {
    if (!model?.metadata) return undefined
    try {
      const parsedMetadata = typeof model.metadata === 'string' ? JSON.parse(model.metadata) : model.metadata
      const camera = parsedMetadata?.defaultCamera ? migrateCameraState(parsedMetadata.defaultCamera) : undefined
      return camera
    } catch (e) {
      return undefined
    }
  }, [model?.metadata])
  
  const autoFocus = useMemo(() => {
    if (!model?.metadata) return true
    try {
      const parsedMetadata = typeof model.metadata === 'string' ? JSON.parse(model.metadata) : model.metadata
      return !parsedMetadata?.defaultCamera
    } catch (e) {
      return true
    }
  }, [model?.metadata])
  
  // Initialize camera from defaultCamera when model loads
  useEffect(() => {
    if (!model?.metadata) return
    
    try {
      const parsedMetadata = typeof model.metadata === 'string' ? JSON.parse(model.metadata) : model.metadata
      const defaultCamera = parsedMetadata?.defaultCamera
      if (defaultCamera) {
        const migratedCamera = migrateCameraState(defaultCamera)
        const { position, quaternion } = cameraStateToQuaternion(migratedCamera)
        setCameraPosition(position)
        setCameraQuaternion(quaternion)
      }
    } catch (e) {
      // Ignore parse error
    }
  }, [model?.metadata])

  // Add default keyframes if none exist
  useEffect(() => {
    if (keyframes.length === 0 && model) {
      if (defaultCamera) {
        // Use model's default camera for the first keyframe
        const { position, quaternion } = cameraStateToQuaternion(defaultCamera)
        
        // Create second camera position - rotate 90 degrees around Y axis
        const secondQuaternion: Quaternion = {
          w: Math.cos(Math.PI / 4),  // cos(45°)
          x: 0,
          y: Math.sin(Math.PI / 4),  // sin(45°) 
          z: 0
        }
        
        // Multiply quaternions to add rotation
        const finalQuaternion: Quaternion = {
          w: quaternion.w * secondQuaternion.w - quaternion.y * secondQuaternion.y,
          x: quaternion.x * secondQuaternion.w + quaternion.z * secondQuaternion.y,
          y: quaternion.y * secondQuaternion.w + quaternion.w * secondQuaternion.y,
          z: quaternion.z * secondQuaternion.w - quaternion.x * secondQuaternion.y
        }
        
        const secondCamera = quaternionToCameraState(
          [position[0] + 5, position[1], position[2]],
          finalQuaternion
        )
        
        setKeyframes([
          {
            id: '1',
            time: 0,
            camera: defaultCamera
          },
          {
            id: '2',
            time: 5,
            camera: secondCamera
          }
        ])
      } else {
        // Create fallback keyframes if model is loaded but no defaultCamera
        setKeyframes([
          {
            id: '1',
            time: 0,
            camera: quaternionToCameraState([0, 0, 5], { w: 1, x: 0, y: 0, z: 0 })
          },
          {
            id: '2',
            time: 5,
            camera: quaternionToCameraState([5, 0, 5], { w: Math.cos(Math.PI / 4), x: 0, y: Math.sin(Math.PI / 4), z: 0 })
          }
        ])
      }
    }
  }, [defaultCamera, model])

  // Enable camera control when keyframes are created
  useEffect(() => {
    if (keyframes.length > 0 && !hasCameraInitialized) {
      setHasCameraInitialized(true)
      // Set initial camera position from first keyframe
      const cameraState = getCameraAtTime(keyframes, currentTime)
      const { position, quaternion } = cameraStateToQuaternion(cameraState)
      setCameraPosition(position)
      setCameraQuaternion(quaternion)
    }
  }, [keyframes, hasCameraInitialized, currentTime])

  // Update camera position based on current time and keyframes
  const updateCameraPosition = useCallback(() => {
    if (keyframes.length === 0) return
    
    const cameraState = getCameraAtTime(keyframes, currentTime)
    const { position, quaternion } = cameraStateToQuaternion(cameraState)
    
    setCameraPosition(position)
    setCameraQuaternion(quaternion)
  }, [keyframes, currentTime])

  // Update time input when current time changes
  useEffect(() => {
    if (!isDraggingPlayhead && !isPlaying) {
      setTimeInputValue(currentTime.toFixed(2))
    }
  }, [currentTime, isDraggingPlayhead, isPlaying])

  // Handle playback
  useEffect(() => {
    let animationFrame: number
    let startTime: number

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp
      const elapsed = (timestamp - startTime) / 1000 // Convert to seconds
      
      if (elapsed >= TIMELINE_DURATION) {
        setIsPlaying(false)
        setCurrentTime(0)
      } else {
        setCurrentTime(elapsed)
        animationFrame = requestAnimationFrame(animate)
      }
    }

    if (isPlaying) {
      animationFrame = requestAnimationFrame(animate)
    }

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame)
      }
    }
  }, [isPlaying])

  // Update camera when time changes during playback
  useEffect(() => {
    if (isPlaying && keyframes.length > 0) {
      updateCameraPosition()
    }
  }, [currentTime, isPlaying, keyframes.length, updateCameraPosition])

  // Handle camera change from viewer
  const handleCameraChange = useCallback((position: [number, number, number], quaternion: Quaternion) => {
    // Don't process camera changes during playback
    if (isPlaying) return
    
    // Update camera position and quaternion
    setCameraPosition(position)
    setCameraQuaternion(quaternion)
    
    // Check if we're editing a keyframe and values changed
    if (editingKeyframe) {
      const { position: kfPos, quaternion: kfQuat } = cameraStateToQuaternion(editingKeyframe.camera)
      const keyframeChanged = 
        Math.abs(kfPos[0] - position[0]) > 0.001 ||
        Math.abs(kfPos[1] - position[1]) > 0.001 ||
        Math.abs(kfPos[2] - position[2]) > 0.001 ||
        Math.abs(kfQuat.w - quaternion.w) > 0.001 ||
        Math.abs(kfQuat.x - quaternion.x) > 0.001 ||
        Math.abs(kfQuat.y - quaternion.y) > 0.001 ||
        Math.abs(kfQuat.z - quaternion.z) > 0.001
      
      setHasUnsavedChanges(keyframeChanged)
    }
  }, [editingKeyframe, isPlaying])

  const handleAddOrSaveKeyframe = () => {
    if (editingKeyframe && hasUnsavedChanges) {
      // Save edited keyframe
      const updatedKeyframe: Keyframe = {
        ...editingKeyframe,
        camera: quaternionToCameraState(cameraPosition, cameraQuaternion)
      }
      
      setKeyframes(prev => prev.map(k => k.id === editingKeyframe.id ? updatedKeyframe : k))
      setEditingKeyframe(updatedKeyframe)
      setHasUnsavedChanges(false)
    } else {
      // Add new keyframe
      const newKeyframe: Keyframe = {
        id: Date.now().toString(),
        time: currentTime,
        camera: quaternionToCameraState(cameraPosition, cameraQuaternion)
      }
      
      setKeyframes(prev => [...prev, newKeyframe].sort((a, b) => a.time - b.time))
      setSelectedKeyframe(newKeyframe.id)
      setEditingKeyframe(newKeyframe)
      setHasUnsavedChanges(false)
    }
  }

  const handleDeleteKeyframe = (id: string) => {
    setKeyframes(prev => prev.filter(k => k.id !== id))
    if (selectedKeyframe === id) {
      setSelectedKeyframe(null)
      setEditingKeyframe(null)
      setHasUnsavedChanges(false)
    }
  }

  const handleCopyKeyframe = () => {
    if (editingKeyframe) {
      setCopiedKeyframe(editingKeyframe)
    }
  }

  const handlePasteKeyframe = () => {
    if (copiedKeyframe) {
      const newKeyframe: Keyframe = {
        ...copiedKeyframe,
        id: Date.now().toString(),
        time: currentTime
      }
      setKeyframes(prev => [...prev, newKeyframe].sort((a, b) => a.time - b.time))
      setSelectedKeyframe(newKeyframe.id)
      setEditingKeyframe(newKeyframe)
      setHasUnsavedChanges(false)
    }
  }

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Don't handle clicks when dragging
    if (isDraggingPlayhead) return
    
    const rect = timelineRef.current?.getBoundingClientRect()
    if (!rect) return
    
    const x = e.clientX - rect.left
    const time = (x / rect.width) * TIMELINE_DURATION
    const clampedTime = Math.max(0, Math.min(time, TIMELINE_DURATION))
    setCurrentTime(clampedTime)
    setTimeInputValue(clampedTime.toFixed(2))
  }

  const handlePlayheadMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsDraggingPlayhead(true)
  }

  useEffect(() => {
    if (!isDraggingPlayhead) return

    const handleMouseMove = (e: MouseEvent) => {
      const rect = timelineRef.current?.getBoundingClientRect()
      if (!rect) return
      
      const x = e.clientX - rect.left
      const time = (x / rect.width) * TIMELINE_DURATION
      const clampedTime = Math.max(0, Math.min(time, TIMELINE_DURATION))
      setCurrentTime(clampedTime)
      setTimeInputValue(clampedTime.toFixed(2))
    }

    const handleMouseUp = () => {
      setIsDraggingPlayhead(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDraggingPlayhead])

  const handleExport = async (format: 'mp4' | 'gif') => {
    if (!canvasRef.current) {
      console.error('Canvas not found')
      return
    }

    if (isExporting) {
      console.warn('Already exporting')
      return
    }

    setIsExporting(true)
    setExportProgress(null)

    // Set up renderAtTime event listener
    const handleRenderAtTime = (e: any) => {
      const time = e.detail.time
      if (keyframes.length > 0) {
        const cameraState = getCameraAtTime(keyframes, time)
        const { position, quaternion } = cameraStateToQuaternion(cameraState)
        setCameraPosition(position)
        setCameraQuaternion(quaternion)
      }
    }
    window.addEventListener('renderAtTime', handleRenderAtTime)

    try {
      // Create recorder if not exists
      if (!recorderRef.current) {
        recorderRef.current = new CanvasRecorder(canvasRef.current)
      }

      // Get quality settings
      const qualitySettings = {
        low: { width: 1280, height: 720, framerate: format === 'gif' ? 10 : 24 },
        medium: { width: 1920, height: 1080, framerate: format === 'gif' ? 10 : 30 },
        high: { width: 2560, height: 1440, framerate: format === 'gif' ? 10 : 60 }
      }

      const settings = qualitySettings[exportQuality]
      
      // First, capture the first frame as a static image
      // Set camera to first keyframe position
      if (keyframes.length > 0) {
        const firstFrame = getCameraAtTime(keyframes, 0)
        const { position, quaternion } = cameraStateToQuaternion(firstFrame)
        setCameraPosition(position)
        setCameraQuaternion(quaternion)
      }
      
      // Initialize and render through timeline to ensure proper rendering
      if (!hasInitialized) {
        // Move through a few frames to trigger rendering
        for (let t = 0; t <= 0.2; t += 0.1) {
          const frameState = getCameraAtTime(keyframes, t)
          const { position, quaternion } = cameraStateToQuaternion(frameState)
          setCameraPosition(position)
          setCameraQuaternion(quaternion)
          await new Promise(resolve => setTimeout(resolve, 100))
        }
        // Go back to first frame
        const firstFrame = getCameraAtTime(keyframes, 0)
        const { position, quaternion } = cameraStateToQuaternion(firstFrame)
        setCameraPosition(position)
        setCameraQuaternion(quaternion)
        setHasInitialized(true)
      }
      
      // Wait for the scene to fully render
      await new Promise(resolve => setTimeout(resolve, 300))
      
      // Capture the first frame as a static image
      const canvas = canvasRef.current
      const thumbnailBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Failed to capture thumbnail'))
            }
          },
          'image/jpeg',
          0.9
        )
      })
      
      // Now record the video/gif
      const options = {
        width: settings.width,
        height: settings.height,
        framerate: settings.framerate,
        duration: TIMELINE_DURATION,
        format
      }

      // Resize the renderer (if DualgsViewer is the active viewer) so each frame
      // captured by the recorder matches the encoder's configured resolution.
      // Without this, VideoFrame(canvas) is the on-screen container size and
      // MP4 frames end up vertically stretched at 1920×1080.
      dualgsViewerRef.current?.setRenderSize?.(settings.width, settings.height)
      // Give the renderer one frame at the new size before capture starts.
      await new Promise(resolve => requestAnimationFrame(() => resolve(null)))

      let result: ArrayBuffer
      try {
        result = await recorderRef.current.startRecording(options, (progress) => {
          setExportProgress(progress)
        })
      } finally {
        dualgsViewerRef.current?.clearRenderSize?.()
      }

      // Convert to blob for download
      const videoBlob = new Blob([result], { 
        type: format === 'mp4' ? 'video/mp4' : 'image/gif' 
      })
      
      // Upload to R2
      setIsUploading(true)
      setUploadProgress(0)

      try {
        // Upload video/gif first
        await mediaApi.uploadMedia(
          id!,
          videoBlob,
          'preview',
          format,
          token || undefined,
          (progress) => setUploadProgress(progress * 0.5) // First 50% for video
        )

        // Upload static thumbnail
        await mediaApi.uploadMedia(
          id!,
          thumbnailBlob,
          'thumbnail',
          'jpg',
          token || undefined,
          (progress) => setUploadProgress(50 + progress * 0.5) // Last 50% for thumbnail
        )
        
        // Show success modal
        setUploadSuccess(true)
        
        // Auto-hide success modal after 5 seconds
        setTimeout(() => {
          setUploadSuccess(false)
        }, 5000)
      } catch (uploadError) {
        console.error('Upload failed:', uploadError)
        
        // Fallback to download both files
        const videoUrl = URL.createObjectURL(videoBlob)
        const videoLink = document.createElement('a')
        videoLink.href = videoUrl
        videoLink.download = `${model?.name || 'model'}_preview.${format}`
        videoLink.click()
        URL.revokeObjectURL(videoUrl)
        
        const thumbnailUrl = URL.createObjectURL(thumbnailBlob)
        const thumbnailLink = document.createElement('a')
        thumbnailLink.href = thumbnailUrl
        thumbnailLink.download = `${model?.name || 'model'}_thumbnail.jpg`
        thumbnailLink.click()
        URL.revokeObjectURL(thumbnailUrl)
        
        alert('Upload failed. Files downloaded to your computer instead.')
      } finally {
        setIsUploading(false)
        setUploadProgress(0)
      }
    } catch (error) {
      console.error('Export failed:', error)
      // Show user-friendly error message
      if (error instanceof Error) {
        if (error.message.includes('codec') || error.message.includes('AVC level')) {
          alert('Video export failed due to codec limitations. Try using a lower quality setting.')
        } else {
          alert(`Export failed: ${error.message}`)
        }
      }
    } finally {
      setIsExporting(false)
      setExportProgress(null)
      // Clean up event listener
      window.removeEventListener('renderAtTime', handleRenderAtTime)
    }
  }

  if (isLoading || !model) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p>Loading model...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate(backPath)}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-semibold">Thumbnail Generator</h1>
              <p className="text-sm text-gray-400">{model?.name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Quality Selector */}
            <select
              value={exportQuality}
              onChange={(e) => setExportQuality(e.target.value as 'low' | 'medium' | 'high')}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={isExporting || isUploading}
            >
              <option value="low">720p</option>
              <option value="medium">1080p</option>
              <option value="high">1440p</option>
            </select>
            
            <button
              onClick={() => handleExport('gif')}
              disabled={isExporting || isUploading}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
            >
              {(isExporting || isUploading) ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              Export GIF
            </button>
            <button
              onClick={() => handleExport('mp4')}
              disabled={isExporting || isUploading}
              className="px-4 py-2 bg-gradient-to-r from-primary-500 to-accent-600 hover:shadow-lg hover:shadow-primary-500/25 disabled:from-gray-600 disabled:to-gray-700 disabled:shadow-none rounded-lg transition-all flex items-center gap-2"
            >
              {(isExporting || isUploading) ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              Export MP4
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col h-[calc(100vh-73px)]">
        {/* Viewer */}
        <div className="flex-1 p-6 min-h-0">
          <div className="h-full bg-gray-800 rounded-lg overflow-hidden">
            {source && (
              <ModelViewer
                ref={dualgsViewerRef}
                source={source}
                className="w-full h-full"
                cameraPosition={keyframes.length > 0 && hasCameraInitialized ? cameraPosition : undefined}
                cameraQuaternion={keyframes.length > 0 && hasCameraInitialized ? cameraQuaternion : undefined}
                onCameraChange={handleCameraChange}
                onCanvasReady={(canvas) => { canvasRef.current = canvas }}
                splat={{
                  controls: true,
                  quality: 'high',
                  defaultCamera,
                  autoFocus,
                  onLoad: () => {
                    // After model loads and defaultCamera has been applied, we can start controlling camera with keyframes
                    // But only if we actually have keyframes created
                    setTimeout(() => {
                      if (keyframes.length > 0) {
                        setHasCameraInitialized(true)
                        // Update camera position to match the current keyframe
                        const cameraState = getCameraAtTime(keyframes, currentTime)
                        const { position, quaternion } = cameraStateToQuaternion(cameraState)
                        setCameraPosition(position)
                        setCameraQuaternion(quaternion)
                      } else {
                        // If no keyframes yet, we'll wait for them to be created
                        // The useEffect that creates keyframes will trigger when defaultCamera is available
                      }
                    }, 100) // Small delay to ensure defaultCamera has been applied
                  },
                }}
              />
            )}
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-gray-800 border-t border-gray-700 p-6">
          
          {/* Controls Row */}
          <div className="flex items-center gap-3 mb-4">
            {/* Play/Pause */}
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="p-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
            >
              {isPlaying ? <Pause size={18} /> : <Play size={18} />}
            </button>
            
            {/* Timestamp Input */}
            <input
              type="text"
              value={timeInputValue}
              onChange={(e) => setTimeInputValue(e.target.value)}
              onBlur={() => {
                const time = parseFloat(timeInputValue) || 0
                const clampedTime = Math.max(0, Math.min(time, TIMELINE_DURATION))
                setCurrentTime(clampedTime)
                setTimeInputValue(clampedTime.toFixed(2))
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const time = parseFloat(timeInputValue) || 0
                  const clampedTime = Math.max(0, Math.min(time, TIMELINE_DURATION))
                  setCurrentTime(clampedTime)
                  setTimeInputValue(clampedTime.toFixed(2))
                  e.currentTarget.blur()
                }
              }}
              className="w-20 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm font-mono text-center focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="0.00"
            />
            <span className="text-xs text-gray-500">s</span>
            
            {/* Position inputs */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Position:</span>
              <input
                type="number"
                value={cameraPosition[0].toFixed(2)}
                onChange={(e) => {
                  const newPos: [number, number, number] = [
                    parseFloat(e.target.value) || 0,
                    cameraPosition[1],
                    cameraPosition[2]
                  ]
                  setCameraPosition(newPos)
                  if (editingKeyframe) setHasUnsavedChanges(true)
                }}
                className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs"
                step="0.1"
                placeholder="X"
              />
              <input
                type="number"
                value={cameraPosition[1].toFixed(2)}
                onChange={(e) => {
                  const newPos: [number, number, number] = [
                    cameraPosition[0],
                    parseFloat(e.target.value) || 0,
                    cameraPosition[2]
                  ]
                  setCameraPosition(newPos)
                  if (editingKeyframe) setHasUnsavedChanges(true)
                }}
                className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs"
                step="0.1"
                placeholder="Y"
              />
              <input
                type="number"
                value={cameraPosition[2].toFixed(2)}
                onChange={(e) => {
                  const newPos: [number, number, number] = [
                    cameraPosition[0],
                    cameraPosition[1],
                    parseFloat(e.target.value) || 0
                  ]
                  setCameraPosition(newPos)
                  if (editingKeyframe) setHasUnsavedChanges(true)
                }}
                className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs"
                step="0.1"
                placeholder="Z"
              />
            </div>
            
            {/* Rotation inputs */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Rotation:</span>
              {(() => {
                const eulerDegrees = quaternionToEulerDegrees(cameraQuaternion)
                return (
                  <>
                    <input
                      type="number"
                      value={eulerDegrees.rx.toFixed(1)}
                      onChange={(e) => {
                        const newQuaternion = eulerDegreesToQuaternion(
                          parseFloat(e.target.value) || 0,
                          eulerDegrees.ry,
                          eulerDegrees.rz
                        )
                        setCameraQuaternion(newQuaternion)
                        if (editingKeyframe) setHasUnsavedChanges(true)
                      }}
                      className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs"
                      step="1"
                      placeholder="X"
                    />
                    <input
                      type="number"
                      value={eulerDegrees.ry.toFixed(1)}
                      onChange={(e) => {
                        const newQuaternion = eulerDegreesToQuaternion(
                          eulerDegrees.rx,
                          parseFloat(e.target.value) || 0,
                          eulerDegrees.rz
                        )
                        setCameraQuaternion(newQuaternion)
                        if (editingKeyframe) setHasUnsavedChanges(true)
                      }}
                      className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs"
                      step="1"
                      placeholder="Y"
                    />
                    <input
                      type="number"
                      value={eulerDegrees.rz.toFixed(1)}
                      onChange={(e) => {
                        const newQuaternion = eulerDegreesToQuaternion(
                          eulerDegrees.rx,
                          eulerDegrees.ry,
                          parseFloat(e.target.value) || 0
                        )
                        setCameraQuaternion(newQuaternion)
                        if (editingKeyframe) setHasUnsavedChanges(true)
                      }}
                      className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs"
                      step="1"
                      placeholder="Z"
                    />
                  </>
                )
              })()}
            </div>
            
            {/* Action buttons */}
            <div className="flex items-center gap-1 ml-auto">
              {editingKeyframe && (
                <>
                  <button
                    onClick={handleCopyKeyframe}
                    className="p-1.5 hover:bg-gray-700 rounded transition-colors"
                    title="Copy"
                  >
                    <Copy size={16} />
                  </button>
                  {copiedKeyframe && (
                    <button
                      onClick={handlePasteKeyframe}
                      className="p-1.5 hover:bg-gray-700 rounded transition-colors"
                      title="Paste"
                    >
                      <Clipboard size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteKeyframe(editingKeyframe.id)}
                    className="p-1.5 hover:bg-gray-700 rounded transition-colors text-red-400"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </>
              )}
              <button
                onClick={handleAddOrSaveKeyframe}
                className={`px-3 py-1.5 rounded transition-colors text-xs flex items-center gap-1 ${
                  editingKeyframe && hasUnsavedChanges
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                {editingKeyframe && hasUnsavedChanges ? (
                  <>
                    <Save size={14} />
                    Save
                  </>
                ) : (
                  <>
                    <Plus size={14} />
                    Add
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Timeline Track */}
          <div
            ref={timelineRef}
            className="relative h-16 bg-gray-900 rounded-lg cursor-pointer overflow-hidden"
            onClick={handleTimelineClick}
          >
            {/* Time ruler */}
            <div className="absolute top-0 left-0 right-0 h-6 border-b border-gray-700 flex items-center">
              {[0, 1, 2, 3, 4, 5].map(second => (
                <div
                  key={second}
                  className="absolute text-xs text-gray-500"
                  style={{ left: `${(second / TIMELINE_DURATION) * 100}%` }}
                >
                  {second}s
                </div>
              ))}
            </div>

            {/* Keyframes */}
            <div className="absolute bottom-0 left-0 right-0 h-10 flex items-center">
              {keyframes.map(keyframe => (
                <div
                  key={keyframe.id}
                  className={`absolute transform -translate-x-1/2 transition-all cursor-pointer ${
                    selectedKeyframe === keyframe.id 
                      ? 'w-4 h-8 -translate-y-1' 
                      : 'w-3 h-6 hover:scale-110'
                  }`}
                  style={{ left: `${(keyframe.time / TIMELINE_DURATION) * 100}%` }}
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedKeyframe(keyframe.id)
                    setCurrentTime(keyframe.time)
                    setEditingKeyframe(keyframe)
                    setHasUnsavedChanges(false)
                    // Directly set camera to keyframe position
                    const { position, quaternion } = cameraStateToQuaternion(keyframe.camera)
                    setCameraPosition(position)
                    setCameraQuaternion(quaternion)
                  }}
                >
                  <div className={`w-full h-full rounded ${
                    selectedKeyframe === keyframe.id 
                      ? 'bg-primary-400 shadow-lg shadow-primary-400/50' 
                      : 'bg-primary-500'
                  }`} />
                  {selectedKeyframe === keyframe.id && (
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-primary-400 font-mono whitespace-nowrap">
                      {keyframe.time.toFixed(2)}s
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Playhead */}
            <div
              className="absolute top-6 bottom-0 w-0.5 bg-white"
              style={{ left: `${(currentTime / TIMELINE_DURATION) * 100}%` }}
            >
              <div 
                className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-white rounded-full cursor-grab hover:scale-125 transition-transform"
                onMouseDown={handlePlayheadMouseDown}
                style={{ cursor: isDraggingPlayhead ? 'grabbing' : 'grab' }}
              />
            </div>
          </div>

        </div>
      </div>

      {/* Export Progress Modal */}
      {(isExporting && exportProgress) && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">Exporting Video...</h3>
            
            <div className="space-y-4">
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-primary-500 to-accent-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${exportProgress.percentage}%` }}
                />
              </div>
              
              <div className="flex justify-between text-sm text-gray-400">
                <span>Frame {exportProgress.frame} of {exportProgress.totalFrames}</span>
                <span>{exportProgress.percentage.toFixed(1)}%</span>
              </div>
              
              {exportProgress.timeRemaining && (
                <div className="text-sm text-gray-400 text-center">
                  Estimated time remaining: {Math.round(exportProgress.timeRemaining / 1000)}s
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload Progress Modal */}
      {isUploading && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">Uploading to Cloud...</h3>
            
            <div className="space-y-4">
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-primary-500 to-accent-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              
              <div className="text-sm text-gray-400 text-center">
                {uploadProgress.toFixed(1)}% uploaded
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {uploadSuccess && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 relative">
            <button
              onClick={() => setUploadSuccess(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
            
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </div>
            
            <h3 className="text-lg font-semibold text-white text-center mb-2">
              Preview Uploaded Successfully!
            </h3>
            
            <p className="text-sm text-gray-400 text-center mb-6">
              The preview video has been generated and set for "{model?.name}".
              It will now appear in the model gallery.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setUploadSuccess(false)
                  navigate(backPath)
                }}
                className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                {backPath.includes('/edit') ? 'Back to Edit Model' : 'Back to Models'}
              </button>
              <button
                onClick={() => setUploadSuccess(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Continue Editing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}